import { useCallback, useRef, useState, type CSSProperties } from "react";
import moment from "moment-timezone";
import { Lock } from "lucide-react";
import ShiftTooltip from "../../../../../components/tooltip/ShiftTooltip";
import BreakOvertimeIndicator from "../../../../../components/BreakOvertimeIndicator";
import SectionInfo from "../../../../../components/SectionInfo";
import { useMenuContext } from "../../../../../hooks/context/useMenuContext";
import { useEventUser } from "../../../../../hooks/useEventUser";
import { useOpenShiftPreferenceStyle } from "../../../../../hooks/styles/useOpenShiftPreferenceStyle";
import { useAddedPayCode } from "../../../../../../../TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { useDefaultLevel } from "../../../../../hooks/useDefaultLevel";
import { snapTimeTo15Minutes } from "../../../../../helpers/eventdisplay/snapTimeTo15Minutes";
import { getSectionDurations } from "../../../../../helpers/data/getSectionDurations";
import { renderSectionsDuration } from "../../../../../helpers/eventdisplay/renderSectionsDuration";
import { cn } from "../../../../../../../../lib/utils";

function OpenShift({ event, className, style, cellWidth }: any) {
    const localRef = useRef<HTMLDivElement>(null);
    const scrollAnimationRef = useRef<number | null>(null);
    const lastScrollTimeRef = useRef<number | null>(null);
    const mousePositionRef = useRef<number>(0);
    const [overrideStyle, setOverrideStyle] = useState<CSSProperties>({});
    const { openMenu } = useMenuContext();
    const user = useEventUser(event?.resourceId);
    const preferenceStyle = useOpenShiftPreferenceStyle();
    const defaultPayCode = useAddedPayCode(user);
    const defaultLevel = useDefaultLevel();

    const resizeData = useRef<{
        direction: "left" | "right";
        initialX: number;
        initialWidth: number;
        initialLeft: number;
        initialScrollLeft: number;
        dayStart: moment.Moment;
        dayEnd: moment.Moment;
        finalStartDate: moment.Moment;
        finalEndDate: moment.Moment;
    } | null>(null);

    const performResizeUpdate = useCallback((currentClientX: number) => {
        if (!resizeData.current || !localRef.current) return;
        const { direction, initialX, initialWidth, initialLeft, initialScrollLeft, dayStart, dayEnd } = resizeData.current;
        const scrollContainer = localRef.current.closest(".schedule-grid-container");
        const currentScrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;
        const scrollDelta = currentScrollLeft - initialScrollLeft;
        const totalPixelChange = (currentClientX - initialX) + scrollDelta;

        const minutesPerPixel = 60 / cellWidth;
        const durationDeltaMinutes = Math.round(totalPixelChange * minutesPerPixel);

        const originalStartDate = moment.parseZone(event.startDate).local();
        const originalEndDate = moment.parseZone(event.endDate).local();

        let newStartDate = originalStartDate.clone();
        let newEndDate = originalEndDate.clone();

        if (direction === "right") {
            let rawEndDate = originalEndDate.clone().add(durationDeltaMinutes, "minutes");
            newEndDate = snapTimeTo15Minutes(rawEndDate);
        } else {
            let rawStartDate = originalStartDate.clone().add(durationDeltaMinutes, "minutes");
            newStartDate = snapTimeTo15Minutes(rawStartDate);
        }

        if (newStartDate.isBefore(dayStart)) newStartDate = dayStart.clone();
        if (newEndDate.isAfter(dayEnd)) newEndDate = dayEnd.clone();

        const minEndDate = newStartDate.clone().add(15, "minutes");
        if (newEndDate.isBefore(minEndDate)) {
            newEndDate = minEndDate.clone();
            if (newEndDate.isAfter(dayEnd)) newEndDate = dayEnd.clone();
        }
        if (newStartDate.isAfter(newEndDate.clone().subtract(15, "minutes"))) {
            newStartDate = newEndDate.clone().subtract(15, "minutes");
            if (newStartDate.isBefore(dayStart)) newStartDate = dayStart.clone();
        }

        let clampedPixelChange: number;
        if (direction === "right") {
            const newDuration = newEndDate.diff(originalStartDate, "minutes");
            const originalDuration = originalEndDate.diff(originalStartDate, "minutes");
            clampedPixelChange = (newDuration - originalDuration) / minutesPerPixel;
        } else {
            const newDuration = originalStartDate.diff(newStartDate, "minutes");
            clampedPixelChange = -newDuration / minutesPerPixel;
        }

        if (direction === "right") {
            const newWidth = Math.max(20, initialWidth + clampedPixelChange);
            setOverrideStyle({ width: `${newWidth}px` });
        } else {
            const newWidth = Math.max(20, initialWidth - clampedPixelChange);
            const newLeft = initialLeft + clampedPixelChange;
            setOverrideStyle({ width: `${newWidth}px`, left: `${newLeft}px` });
        }

        resizeData.current.finalStartDate = newStartDate;
        resizeData.current.finalEndDate = newEndDate;
    },
        [event.startDate, event.endDate, cellWidth],
    );

    const stopAutoScroll = useCallback(() => {
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
            scrollAnimationRef.current = null;
        }
        lastScrollTimeRef.current = null;
    }, []);

    // Speed scales with real elapsed time and how far past the threshold the cursor is,
    // instead of a flat px-per-frame step - keeps the scroll feeling smooth/eased rather
    // than snapping straight to a constant speed the moment the cursor crosses the edge.
    const MAX_SCROLL_SPEED = 600;
    // Floor so speed doesn't feel imperceptibly slow right at the edge of the trigger zone.
    const MIN_INTENSITY = 0.4;

    const autoScrollLoop = useCallback((timestamp: number) => {
        if (!resizeData.current || !localRef.current) {
            stopAutoScroll();
            return;
        }
        const scrollContainer = localRef.current.closest(".schedule-grid-container");
        if (!scrollContainer) return;
        const containerRect = scrollContainer.getBoundingClientRect();
        const mouseX = mousePositionRef.current;
        const scrollThreshold = 50;

        const last = lastScrollTimeRef.current;
        const deltaSeconds = last == null ? (1 / 60) : Math.min(timestamp - last, 100) / 1000;
        lastScrollTimeRef.current = timestamp;

        const rightOvershoot = mouseX - (containerRect.right - scrollThreshold);
        const leftOvershoot = containerRect.left + scrollThreshold - mouseX;
        let scrolled = false;
        if (rightOvershoot > 0) {
            const intensity = MIN_INTENSITY + (1 - MIN_INTENSITY) * Math.min(1, rightOvershoot / scrollThreshold);
            scrollContainer.scrollLeft += MAX_SCROLL_SPEED * intensity * deltaSeconds;
            scrolled = true;
        } else if (leftOvershoot > 0) {
            const intensity = MIN_INTENSITY + (1 - MIN_INTENSITY) * Math.min(1, leftOvershoot / scrollThreshold);
            scrollContainer.scrollLeft -= MAX_SCROLL_SPEED * intensity * deltaSeconds;
            scrolled = true;
        }
        performResizeUpdate(mouseX);
        if (scrolled) scrollAnimationRef.current = requestAnimationFrame(autoScrollLoop);
        else stopAutoScroll();
    }, [stopAutoScroll, performResizeUpdate]);


    const onContextMenu = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        openMenu({
            id: "open-shift-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: event
        });
    }, [openMenu, event]);

    const { overtimeDuration, breakDuration } = getSectionDurations(event?.sections);


    return (
        <ShiftTooltip data={event}>
            <div
                className={cn(
                    "work-block tw-bg-neutral-100 tw-rounded-sm tw-px-3 tw-py-2 tw-cursor-pointer",
                    "tw-flex  tw-items-center tw-absolute tw-border-2 tw-z-10 tw-border-dashed",
                    className || "",
                )}
                style={{ ...style, ...overrideStyle, ...preferenceStyle }}
                onContextMenu={onContextMenu}
            >
                <div className="tw-w-full tw-flex tw-justify-between tw-items-center tw-gap-3">
                    <div className="tw-flex tw-flex-col tw-w-full">
                        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                            <div className="tw-font-bold tw-text-sm tw-flex tw-items-center tw-gap-2 tw-truncate">
                                {event?.locked && (<Lock size={16} strokeWidth={3} />)}
                                {moment(event.startDate).format("h:mma")}
                                {" - "}
                                {moment(event.endDate).format("h:mma")}
                                <span className="tw-font-normal tw-text-xs tw-text-neutral-600">
                                    {"("}
                                    {renderSectionsDuration(
                                        moment(event.endDate).diff(moment(event.startDate), "seconds"),
                                        event.sections
                                    )}
                                    {")"}
                                </span>
                            </div>

                            <BreakOvertimeIndicator
                                breakDuration={breakDuration}
                                overtimeDuration={overtimeDuration}
                                size={17}
                            />
                        </div>

                        <SectionInfo
                            section={event?.sections?.[0]}
                            payCode={defaultPayCode?.code}
                            defaultLevel={defaultLevel}
                            className="tw-pt-0.5"
                        />
                    </div>
                </div>
            </div>
        </ShiftTooltip>
    );
}

export default OpenShift;
